export interface ReviewSection {
  heading: string;
  paragraphs: string[];
}

export interface Review {
  id: string;
  lead: string;
  verdict: string;
  scoreDetail: { label: string; value: number }[];
  sections: ReviewSection[];
  scenes: string[];
}

const sceneSunset = "https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20scene%20art%20of%20a%20quiet%20residential%20neighborhood%20at%20sunset%2C%20warm%20golden%20light%20through%20street%20lamps%2C%20soft%20bloom%20and%20film%20grain%2C%20nostalgic%20peaceful%20mood%2C%20editorial%20visual%20novel%20background%20art&width=1200&height=700&seq=scene-sunset-001&orientation=landscape";
const sceneIslands = "https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20scene%20art%20of%20floating%20islands%20above%20a%20glowing%20sea%20at%20dusk%2C%20warm%20gold%20and%20crimson%20sky%2C%20mist%20and%20light%20beams%2C%20epic%20fantasy%20mood%2C%20visual%20novel%20background%20art&width=1200&height=700&seq=scene-islands-001&orientation=landscape";
const sceneCityNight = "https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20scene%20art%20of%20a%20rainy%20city%20street%20at%20night%20with%20neon%20reflections%20and%20warm%20window%20light%2C%20moody%20noir%20tone%2C%20visual%20novel%20background%20art&width=1200&height=700&seq=scene-city-001&orientation=landscape";
const sceneTemple = "https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20scene%20art%20of%20an%20old%20temple%20courtyard%20under%20moonlight%20with%20paper%20lanterns%20and%20cherry%20blossoms%2C%20serene%20mystical%20mood%2C%20warm%20amber%20tones%2C%20visual%20novel%20background%20art&width=1200&height=700&seq=scene-temple-001&orientation=landscape";

export const reviews: Record<string, Review> = {
  "sakura-convenience": {
    id: "sakura-convenience",
    lead: "《樱花便利店物语》没有宏大的冒险，只有一家开到深夜的便利店，和几个在灯光下慢慢靠近的人。它用最普通的日常，写出了今年最动人的成人游戏叙事。",
    verdict: "无论你是刚接触成人游戏的新手，还是想找一部能安静看完的好作品的老玩家，它都值得放进清单。",
    scoreDetail: [
      { label: "剧情", value: 9.2 },
      { label: "美术", value: 8.8 },
      { label: "演出", value: 8.5 },
      { label: "音乐", value: 8.9 },
      { label: "玩法", value: 8.0 },
    ],
    sections: [
      { heading: "把日常写成了诗", paragraphs: ["故事围绕一间开在樱花小镇的深夜便利店展开，主角在夜班中陆续遇见几位性格迥异的常客。没有天降奇遇，也没有拯救世界，所有关系都从一句问候、一份关东煮、一场雨开始慢慢生根。", "真正高明的地方在于，它相信日常本身有力量。两人在柜台前的闲聊、一起躲雨的小巷、下班后送对方回家的那段路——这些看似无足轻重的片段，被细腻的对白和恰到好处的留白串联起来，最后在某个节点一次性击中你。"] },
      { heading: "低门槛，但不浅薄", paragraphs: ["作为入门推荐，它几乎没有系统负担：选择支线、推进关系、抵达结局，节奏被控制在舒适的范围内。但这不意味着内容浅薄——多结局设置让每个角色都有完整的个人弧光，二周目开启后你才会发现很多伏笔早已埋下。", "对新手而言，它是绝佳的‘第一站’；对老玩家而言，它是一部值得慢下来重读的作品。"] },
      { heading: "画面与音乐：温柔的氛围感", paragraphs: ["暖色调的立绘与背景营造出被灯光包裹的安心感，配乐则以钢琴与弦乐为主，和深夜便利店的意象高度契合。整体演出偏向静态，但对这类题材而言，克制反而成了优点。"] },
    ],
    scenes: [sceneSunset, sceneTemple],
  },
  "echo-of-starsea": {
    id: "echo-of-starsea",
    lead: "《星海的回响》是那种玩完后会在心里空落落很久的作品。它用一座漂浮在星海上的群岛，装下了关于成长、离别与爱的全部命题。",
    verdict: "近年成人游戏中最接近「神作」之名的长剧，值得你为它腾出一整个周末。",
    scoreDetail: [
      { label: "剧情", value: 9.6 },
      { label: "美术", value: 9.3 },
      { label: "演出", value: 9.0 },
      { label: "音乐", value: 9.4 },
      { label: "玩法", value: 8.8 },
    ],
    sections: [
      { heading: "史诗级的剧本体量", paragraphs: ["从少女踏上第一座浮岛开始，到横跨整个星海的终章，剧本用惊人的密度勾勒出宏大的世界观。每一个主要角色都有完整的成长轨迹，连看似边缘的配角都拥有令人动容的支线。", "它的情感爆发不是靠强行煽情，而是靠漫长铺垫后的自然涌现。当你意识到某个伏笔的答案时，那种‘原来如此’的后劲会持续很久。"] },
      { heading: "角色塑造的教科书", paragraphs: ["几乎所有攻略角色都值得你单独走完一整条线。她们不是标签的集合，而是拥有欲望、恐惧与软肋的人。关系的发展循序渐进，从试探到信任再到更深层次的交付，节奏拿捏得极为老练。"] },
      { heading: "当之无愧的视听盛宴", paragraphs: ["群星闪耀的浮岛景观、华丽却不堆砌的战斗演出、以及贯穿始终的史诗级配乐，共同构成了极高的视听完成度。唯一的代价是它很长——全支线通关需要你足够的耐心。"] },
    ],
    scenes: [sceneIslands, sceneSunset],
  },
  "eden-archipelago": {
    id: "eden-archipelago",
    lead: "《伊甸群岛》重新定义了成人游戏的自由度。它把探索、叙事与情感编织进一座可以自由航行的群岛世界，让你真正拥有选择的重量。",
    verdict: "追求自由探索与完整世界观构建的玩家，几乎找不到比它更值得投入时间的作品。",
    scoreDetail: [
      { label: "剧情", value: 9.3 },
      { label: "美术", value: 9.2 },
      { label: "演出", value: 8.9 },
      { label: "音乐", value: 9.1 },
      { label: "玩法", value: 9.5 },
    ],
    sections: [
      { heading: "自由，是有重量的", paragraphs: ["你可以决定先探索哪座岛、先认识哪个人、先解开哪个谜题。每条路线都会带来不同的信息与关系走向，而最终结局并非简单的‘选A或B’，而是由你一路的选择共同塑造。", "这种设计让每次航行都有意义——你不再是看故事的人，而是故事的参与者。"] },
      { heading: "值得驻足的探索感", paragraphs: ["岛屿之间藏着大量细节：遗落的信件、隐晦的壁画、需要特定时间才会出现的人物。探索的回报异常丰厚，让人心甘情愿在群岛间反复航行，只为了拼出世界观的全貌。"] },
      { heading: "十年难遇的标杆", paragraphs: ["它并非没有门槛——上手需要耐心，信息量也需要消化。但当你真正融入这片群岛，就会发现它对‘自由’的诠释已经远超类型常规，是值得被记住的里程碑作品。"] },
    ],
    scenes: [sceneIslands, sceneCityNight],
  },
  "fog-city-riddle": {
    id: "fog-city-riddle",
    lead: "《雾都迷局》用一场雾气弥漫的连环谜案，证明成人游戏同样能把推理做得严谨而迷人。",
    verdict: "剧情党与推理爱好者不容错过的口碑之作，情感线与谜题设计同样出彩。",
    scoreDetail: [
      { label: "剧情", value: 9.1 },
      { label: "美术", value: 8.7 },
      { label: "演出", value: 8.6 },
      { label: "音乐", value: 8.8 },
      { label: "玩法", value: 8.9 },
    ],
    sections: [
      { heading: "环环相扣的谜题", paragraphs: ["案件与案件之间互为伏笔，线索的分布足够公平，玩家完全可以依靠观察推理出真相，而非被动等待揭晓。多周目会解锁更多细节，让‘再玩一遍’成为一种享受。", "它没有为了反转而反转，每一步都踩在逻辑的基石上，这种克制在同类作品中相当难得。"] },
      { heading: "雾中的情感", paragraphs: ["在冷硬的推理外壳下，人物关系意外地温热。侦探与搭档之间若即若离的张力，以及案件背后裹藏的情感动机，让整个故事更有血肉。"] },
      { heading: "氛围是最大武器", paragraphs: ["雨夜的街道、昏黄的街灯、随风飘散的雾，配合低沉的配乐，把整座城市塑造成一个会呼吸的角色。氛围感是它最令人印象深刻的记忆点。"] },
    ],
    scenes: [sceneCityNight, sceneTemple],
  },
  "midnight-office": {
    id: "midnight-office",
    lead: "《深夜写字楼》把成年人下班后的疲惫、欲望与一点点心动，坦诚地摆在办公桌和窗边夜色里。",
    verdict: "一部短而精的都市成熟之作，适合在周末夜晚一口气读完。",
    scoreDetail: [
      { label: "剧情", value: 8.8 },
      { label: "美术", value: 8.5 },
      { label: "演出", value: 8.6 },
      { label: "音乐", value: 8.4 },
      { label: "玩法", value: 7.8 },
    ],
    sections: [
      { heading: "真实的成年人心事", paragraphs: ["故事发生在加班的深夜写字楼里，几个被生活推着走的人，在一盏盏亮着的电脑屏幕前慢慢走近。对白没有粉饰，把成年人的疲惫、试探与逞强写得非常真实，甚至让人会心一笑。", "它最大的优点是克制——不夸张、不狗血，让暧昧在沉默与眼神之间自然发生。"] },
      { heading: "短而精的体量", paragraphs: ["单周目体量友好，一个晚上就能走完一段完整的情感旅程。分支设置得当，不同选择会导向气质迥异的结局，值得回头补完其他路线。"] },
    ],
    scenes: [sceneCityNight, sceneSunset],
  },
  "throne-of-night": {
    id: "throne-of-night",
    lead: "《暗夜王座》不是一部让人舒服的作品，但它锋利得让人无法移开视线。",
    verdict: "喜欢沉重、大胆叙事的玩家会视之为宝藏；对题材敏感者请谨慎入坑。",
    scoreDetail: [
      { label: "剧情", value: 9.0 },
      { label: "美术", value: 8.8 },
      { label: "演出", value: 9.0 },
      { label: "音乐", value: 9.2 },
      { label: "玩法", value: 8.2 },
    ],
    sections: [
      { heading: "直面欲望与权力的寓言", paragraphs: ["故事以一座被荆棘环绕的王座为起点，逐步展开关于权力、欲望与自我牺牲的黑暗寓言。它不回避人性的阴暗面，甚至主动去触碰那些被主流叙事回避的命题。", "这种‘不留余地’的坦诚，让它在口碑上呈现明显的两极——喜欢的人认为它有深度，不喜欢的人觉得它沉重。"] },
      { heading: "压迫感极强的演出", paragraphs: ["低沉的配乐与极具冲击力的演出共同营造出令人窒息的氛围，让每一次抉择都充满张力。它更适合带着足够心理准备、且偏好严肃题材的玩家。"] },
    ],
    scenes: [sceneTemple, sceneIslands],
  },
  "starlight-academy": {
    id: "starlight-academy",
    lead: "《星轨学园》证明了后宫题材可以做得既有广度又有深度——每一位女主角，都值得一整条精心打磨的个人线。",
    verdict: "角色厨的盛宴，声优阵容豪华，周目耐玩度极高，是多线题材的集大成者。",
    scoreDetail: [
      { label: "剧情", value: 8.9 },
      { label: "美术", value: 8.8 },
      { label: "演出", value: 8.7 },
      { label: "音乐", value: 8.6 },
      { label: "玩法", value: 8.8 },
    ],
    sections: [
      { heading: "每人一条完整的线", paragraphs: ["与许多‘后宫只是换张立绘’的作品不同，星轨学园的每一位女主角都拥有专属的剧情线、独立的冲突与成长。选择不同攻略对象，读到的几乎是气质完全不同的故事。", "这种认真对待每个角色的态度，让它在同类作品中脱颖而出。"] },
      { heading: "豪华阵容与耐玩度", paragraphs: ["从画面到配音都维持着相当高的水准，豪华声优阵容让角色更加鲜活。由于个人线体量充足，整体通关时长较长，但每一周目都不会让人感到重复。"] },
    ],
    scenes: [sceneTemple, sceneCityNight],
  },
};

export function getReview(id: string): Review | undefined {
  return reviews[id];
}